import { MaterialIcon } from '@components/common/MaterialIcon';
import { SETTINGS_PAGE_ROUTE } from '@constants/routes';
import { Menu, MenuButton } from '@headlessui/react';
import { getBadgeDisplayValue } from '@helpers/notificationHelpers';
import { useModal } from '@hooks/modal/useModal';
import { useNotificationCounts } from '@hooks/notifications/useNotificationCounts';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import { useSidebar } from '@hooks/useSidebar';
import { useWebScraperNotifications } from '@hooks/web-scraper/useWebScraperNotifications';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { NotificationCenterDropdown } from './dropdown/NotificationCenterDropdown';
import { StudioSwitcherTabs } from './studio-tabs/StudioSwitcherTabs';

export const AppBar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { hasHomeV2 } = usePlatformFeatures();
  const { toggleSideBar } = useSidebar();
  const {
    user: { currentUser },
  } = useMainSelector();

  const { trainAiCount, knowledgeArticleCount } = useNotificationCounts();
  useWebScraperNotifications();

  const shouldDisplaySettingsMenu = currentUser?.isSuperAdmin ?? false;

  const shouldShowNotificationBell =
    trainAiCount !== null || knowledgeArticleCount !== null;
  const notificationBadgeDisplay = getBadgeDisplayValue(
    (trainAiCount ?? 0) + (knowledgeArticleCount ?? 0),
  );

  return (
    <nav className='flex items-center w-full bg-slate-50 border-b border-gray-200 shadow-bottom-sidebar z-10 py-1 lg:py-2 px-4 justify-end gap-4 h-[60px]'>
      {/* Mobile-only: open the overlay sidebar (it is fully hidden on mobile) */}
      <div
        className='md:hidden mr-auto cursor-pointer flex items-center justify-center'
        onClick={() => toggleSideBar()}>
        <MaterialIcon
          classes={['text-lg', 'text-gray-500', 'leading-none']}
          icon={'menu'}
          type={'rounded'}
        />
      </div>
      <div className='min-w-0 flex-shrink'>
        <StudioSwitcherTabs />
      </div>
      <div className='flex items-center flex-shrink-0'>
        {shouldDisplaySettingsMenu && (
          <button
            type='button'
            onClick={() => navigate(SETTINGS_PAGE_ROUTE)}
            className='group/cog flex items-center justify-center w-[32px] h-[32px] md:w-[38px] md:h-[38px] mr-2 lg:mr-3 bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus-visible:outline-none'>
            <MaterialIcon
              type='outlined'
              classes={[
                'text-base',
                'md:text-lg',
                'group-hover/cog:text-primary-base',
                'text-gray-500',
              ]}
              icon={'settings'}
            />
          </button>
        )}
        {/* Suggestion box - every studio, every user, but only on Home v2: a v1
         * company still reaches it from the Dashboard sidebar, and Home v2 is what
         * hides that page. The amber halo is a plain box-shadow, not a Tailwind
         * ring: the modal hands focus back to this button on close, and ring
         * utilities left a stray dark ring there. Same reason it is labelled with
         * aria-label and not title. */}
        {hasHomeV2 && (
          <button
            type='button'
            aria-label={t('suggestion-box.modal.title')}
            onClick={() => showModal('suggestion-box')}
            className='group/idea flex items-center justify-center w-[32px] h-[32px] md:w-[38px] md:h-[38px] mr-2 lg:mr-3 bg-white border border-gray-200 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-amber-50 hover:border-amber-300 hover:shadow-[0_0_0_3px_rgba(245,158,11,0.18)] focus:outline-none focus-visible:outline-none focus-visible:border-amber-300 focus-visible:shadow-[0_0_0_3px_rgba(245,158,11,0.18)]'>
            <MaterialIcon
              type='outlined'
              classes={[
                'text-base',
                'md:text-lg',
                'group-hover/idea:text-amber-600',
                'text-amber-500',
              ]}
              icon={'lightbulb'}
            />
          </button>
        )}
        {/* Live-chat handover notifications are intentionally not surfaced here.
         * The old bell (gated on CONVERSATION_MANAGE) was removed due to low usage.
         * The socket infrastructure and useChatNotifications hook remain active for
         * the ChatCard sidebar counts and browser tab title. */}
        {shouldShowNotificationBell && (
          <Menu as='div' className='relative'>
            <MenuButton className='group/bell relative cursor-pointer bg-white border border-gray-200 rounded-lg w-[32px] h-[32px] md:w-[38px] md:h-[38px] focus:outline-none focus-visible:outline-none flex items-center justify-center'>
              <MaterialIcon
                type='outlined'
                classes={[
                  'text-base',
                  'md:text-lg',
                  'group-hover/bell:text-primary-base',
                  'group-data-[open]/bell:text-primary-base',
                  'text-gray-500',
                ]}
                icon={'notifications'}
              />
              {notificationBadgeDisplay !== undefined && (
                <span className='absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 w-[18px] h-[18px] grid place-items-center bg-red-base text-white text-timestamp font-mono font-normal rounded-full leading-none'>
                  {notificationBadgeDisplay}
                </span>
              )}
            </MenuButton>
            <NotificationCenterDropdown
              trainAiCount={trainAiCount}
              knowledgeArticleCount={knowledgeArticleCount}
            />
          </Menu>
        )}
      </div>
    </nav>
  );
};
